import { type Monitor, batch } from "@suse-o11y/signal";
import type { ZodType, ZodTypeDef } from "zod";

import { fromJSON } from "~/tools/from-json";
import { isPresent } from "~/tools/is-present";
import { isString } from "~/tools/is-string";
import { isUndefined } from "~/tools/is-undefined";
import { times } from "~/tools/times";
import { toJSON } from "~/tools/to-json";

import { PersistentStorageState } from "./_persistent-storage-state";

/**
 * Provides a wrapper around the browser's `Storage` API (such as `localStorage` or `sessionStorage`)
 * with additional state management and schema validation capabilities.
 *
 * `PersistentStorage` synchronizes its internal state with the underlying storage and listens for
 * storage events to keep data consistent across browser tabs. It supports storing, retrieving,
 * removing, and clearing items, as well as filtering items based on a predicate.
 */
export class PersistentStorage {
	private readonly state = new PersistentStorageState();

	/**
	 * Unsubscribes from storage events to stop observing changes.
	 */
	public readonly unobserve: VoidFunction;

	public constructor(private readonly storage: Storage) {
		const handler = ({ storageArea, key, newValue }: StorageEvent): void => {
			// skip events that are not from this storage
			if (storageArea !== this.storage) {
				return;
			}

			// the storage was cleared
			if (key == null) {
				this.state.clear();

				return;
			}

			// the item was removed
			if (newValue == null) {
				this.state.removeItem(key);

				return;
			}

			this.state.setItem(key, newValue);
		};

		// synchronize the storage updates with the internal state
		// biome-ignore lint/style/noRestrictedGlobals: encapsulation
		window.addEventListener("storage", handler);

		// biome-ignore lint/style/noRestrictedGlobals: encapsulation
		this.unobserve = () => window.removeEventListener("storage", handler);
	}

	/**
	 * Reactively retrieves a raw string item from the storage so whenever the item's value changes it will be updated.
	 *
	 * @param monitor - monitor provided by a hosting component/hook for reactivity
	 * @param key - the key of the item to retrieve
	 * @returns the raw string value of the item or `undefined` if the item does not exist
	 */
	public getItem(monitor: Monitor, key: string): undefined | string;

	/**
	 * Reactively retrieves a parsed value item from the storage so whenever the item's value changes it will be updated.
	 *
	 * @param monitor - monitor provided by a hosting component/hook for reactivity
	 * @param key - the key of the item to retrieve
	 * @param schema - the Zod schema to validate the item against
	 * @returns the parsed value of the item or `undefined` if the item does not exist or does not match the schema
	 */
	public getItem<TValue>(
		monitor: Monitor,
		key: string,
		schema: ZodType<TValue, ZodTypeDef, unknown>,
	): undefined | TValue;

	public getItem<TValue>(monitor: Monitor, key: string, schema?: ZodType<TValue>): undefined | string | TValue {
		// read the signal value first and then fall back to the storage
		// it allows to populate the PersistentStorage from the Storage on demand
		const serialized = this.state.getItem(monitor, key) ?? this.storage.getItem(key);

		if (serialized == null) {
			return undefined;
		}

		if (schema == null) {
			return serialized;
		}

		const [error, value] = fromJSON(serialized, schema);

		if (error != null) {
			return undefined;
		}

		return value;
	}

	/**
	 * Sets the item's serialized value.
	 *
	 * @param key - the key of the item to set
	 * @param value - the value of the item to set
	 * @param serialize - an optional function to serialize the value before storing it.
	 *                    Defaults to `identity` when value is `string` otherwise to `toJSON`.
	 */
	public setItem<TValue>(key: string, value: TValue, serialize?: (value: TValue) => string): void {
		const serialized =
			serialize != null ? serialize(value) : isString(value) ? value : isUndefined(value) ? undefined : toJSON(value);

		if (isString(serialized)) {
			this.storage.setItem(key, serialized);
			this.state.setItem(key, serialized);
		} else {
			this.removeItem(key);
		}
	}

	/**
	 * Removes an item from the storage.
	 *
	 * @param key - the key of the item to remove
	 */
	public removeItem(key: string): void {
		this.storage.removeItem(key);
		this.state.removeItem(key);
	}

	/**
	 * Clears all items from the storage.
	 */
	public clear(): void {
		this.storage.clear();
		this.state.clear();
	}

	/**
	 * Filters items in the storage based on a predicate function. The same as `Array#filter()`, but for the storage.
	 *
	 * @param predicate A function that takes a key and returns `true` if the item should be kept, or `false` if it should be removed.
	 */
	public filterItems(predicate: (key: string) => boolean): void {
		batch(() => {
			// extract all keys before mutating the storage
			const keys = times(this.storage.length, (index) => this.storage.key(index)).filter(isPresent);

			for (const key of keys) {
				if (!predicate(key)) {
					this.removeItem(key);
				}
			}
		});
	}
}
